/**
 * Centralized Categories Configuration
 * 
 * Single source of truth for all marketplace categories.
 * Used by: MarketplaceLanding, MarketplaceSearch, MarketplaceTab, ExperienceDetail
 * 
 * To add a new category:
 * 1. Add to CATEGORIES array with unique id (slug), name, icon, description, and image
 * 2. All pages will automatically pick up the new category
 */

export const CATEGORIES = [
  { 
    id: 'all', 
    name: 'All', 
    icon: '✨',
    description: 'Browse all experiences',
    image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=300&h=200&fit=crop'
  },
  { 
    id: 'boat-tours', 
    name: 'Boat Tours', 
    icon: '🚤',
    description: 'Cruises, sailing, yacht charters, and water excursions',
    image: 'https://images.unsplash.com/photo-1648997934392-7213a9ce50b7?w=300&h=200&fit=crop'
  },
  { 
    id: 'food-drink', 
    name: 'Food & Drink', 
    icon: '🍷',
    description: 'Culinary tours, wine tasting, cooking classes, and food experiences',
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=300&h=200&fit=crop'
  },
  { 
    id: 'adventure', 
    name: 'Adventure', 
    icon: '🏔️',
    description: 'Hiking, climbing, zip-lining, and outdoor thrills',
    image: 'https://images.unsplash.com/photo-1527004013197-933c4bb611b3?w=300&h=200&fit=crop'
  },
  { 
    id: 'sightseeing', 
    name: 'Sightseeing', 
    icon: '🏛️',
    description: 'City tours, landmarks, walking tours, and guided visits',
    image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=300&h=200&fit=crop'
  },
  { 
    id: 'nature-wildlife', 
    name: 'Nature', 
    icon: '🌿',
    description: 'Wildlife safaris, national parks, eco-tours, and nature walks',
    image: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=300&h=200&fit=crop'
  },
  { 
    id: 'water-sports', 
    name: 'Water Sports', 
    icon: '🏄',
    description: 'Surfing, diving, snorkeling, kayaking, and jet skiing',
    image: 'https://images.unsplash.com/photo-1530053969600-caed2596d242?w=300&h=200&fit=crop'
  },
  { 
    id: 'cultural', 
    name: 'Cultural', 
    icon: '🎭',
    description: 'Museums, heritage sites, art tours, and local traditions',
    image: 'https://images.unsplash.com/photo-1518998053901-5348d3961a04?w=300&h=200&fit=crop'
  },
  { 
    id: 'wellness-spa', 
    name: 'Wellness & Spa', 
    icon: '🧘',
    description: 'Spa treatments, yoga retreats, meditation, and health experiences',
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=300&h=200&fit=crop'
  },
  { 
    id: 'classes-workshops', 
    name: 'Classes', 
    icon: '🎨',
    description: 'Art classes, photography workshops, craft sessions, and skill building',
    image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=300&h=200&fit=crop'
  },
  { 
    id: 'nightlife', 
    name: 'Nightlife', 
    icon: '🌃',
    description: 'Bar crawls, club tours, night markets, and evening entertainment',
    image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=300&h=200&fit=crop'
  },
  { 
    id: 'desert-safari', 
    name: 'Desert & Safari', 
    icon: '🐪',
    description: 'Desert tours, dune bashing, camel rides, and safari adventures',
    image: 'https://images.unsplash.com/photo-1645616672289-3681f160db22?w=300&h=200&fit=crop'
  },
  { 
    id: 'aerial', 
    name: 'Aerial', 
    icon: '🎈',
    description: 'Hot air balloons, helicopter tours, skydiving, and paragliding',
    image: 'https://images.unsplash.com/photo-1507608616759-54f48f0af0ee?w=300&h=200&fit=crop'
  },
  { 
    id: 'sports-fitness', 
    name: 'Sports', 
    icon: '⛳',
    description: 'Golf, tennis, cycling tours, and athletic activities',
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=300&h=200&fit=crop'
  },
  { 
    id: 'family-kids', 
    name: 'Family', 
    icon: '👨‍👩‍👧‍👦',
    description: 'Theme parks, kid-friendly tours, and family activities',
    image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=300&h=200&fit=crop'
  },
  { 
    id: 'romantic', 
    name: 'Romantic', 
    icon: '💕',
    description: 'Couples experiences, sunset cruises, and romantic getaways',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&h=200&fit=crop'
  },
  { 
    id: 'transportation', 
    name: 'Transfers', 
    icon: '🚐',
    description: 'Airport transfers, private drivers, and luxury transportation',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=300&h=200&fit=crop'
  },
  { 
    id: 'winter-sports', 
    name: 'Winter Sports', 
    icon: '⛷️',
    description: 'Skiing, snowboarding, ice skating, and snow activities',
    image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=300&h=200&fit=crop'
  },
];

// Quick filter categories for the search page - show all categories
export const QUICK_FILTER_CATEGORIES = CATEGORIES;

// Get category by ID
export const getCategoryById = (id) => {
  return CATEGORIES.find(cat => cat.id === id) || null;
};

// Get category name by ID
export const getCategoryName = (id) => {
  const category = getCategoryById(id);
  return category ? category.name : id;
};

// Get category icon by ID
export const getCategoryIcon = (id) => {
  const category = getCategoryById(id);
  return category ? category.icon : '📍';
};

// Categories without 'all' for dropdowns/selects
export const CATEGORY_OPTIONS = CATEGORIES.filter(cat => cat.id !== 'all');

export default CATEGORIES;
